<template>
  <div class="device-blacklist">
    <div class="page-header">
      <div>
        <h2>商城设备黑名单</h2>
        <p>黑名单设备只隐藏用户端商城入口，不会撤销手机号白名单授予的商城访问权限。</p>
      </div>
      <el-button type="primary" @click="openCreateDialog">添加设备</el-button>
    </div>

    <el-card shadow="never">
      <el-form :inline="true" :model="query" class="search-form">
        <el-form-item label="设备号">
          <el-input
            v-model="query.deviceId"
            clearable
            placeholder="请输入设备号"
            @keyup.enter="handleSearch"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="query.enabled" clearable placeholder="全部状态" style="width: 130px">
            <el-option label="已拉黑" :value="1" />
            <el-option label="未拉黑" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table
        v-loading="loading"
        :data="records"
        border
        stripe
        empty-text="暂无设备黑名单记录"
      >
        <el-table-column prop="deviceId" label="设备号" min-width="190" />
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.enabled === 1 ? 'danger' : 'info'">
              {{ row.enabled === 1 ? '已拉黑' : '未拉黑' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="当前绑定用户" min-width="170">
          <template #default="{ row }">
            <div v-if="row.currentInspectors && row.currentInspectors.length" class="inspector-list">
              <div
                v-for="inspector in row.currentInspectors"
                :key="inspector.id"
                class="inspector-item"
              >
                <span>{{ inspector.nickName || '-' }}</span>
                <small>ID：{{ inspector.id }}</small>
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="手机号" width="150">
          <template #default="{ row }">
            <div v-if="row.currentInspectors && row.currentInspectors.length">
              <div v-for="inspector in row.currentInspectors" :key="inspector.id">
                {{ maskPhone(inspector.phone) }}
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">{{ row.remark || '-' }}</template>
        </el-table-column>
        <el-table-column prop="updateTime" label="更新时间" width="170">
          <template #default="{ row }">{{ row.updateTime || row.createTime || '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="190" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="editRemark(row)">编辑备注</el-button>
            <el-button
              size="small"
              :type="row.enabled === 1 ? 'warning' : 'success'"
              @click="toggleStatus(row)"
            >
              {{ row.enabled === 1 ? '取消拉黑' : '拉黑设备' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="query.current"
          :page-size="query.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <el-dialog v-model="createVisible" title="添加设备黑名单" width="520px">
      <el-form ref="createFormRef" :model="createForm" :rules="createRules" label-width="90px">
        <el-form-item label="设备号" prop="deviceText">
          <el-input
            v-model="createForm.deviceText"
            type="textarea"
            :rows="6"
            maxlength="5000"
            show-word-limit
            placeholder="每行一个设备号，也可使用逗号分隔；单次最多100个"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="createForm.remark"
            maxlength="255"
            show-word-limit
            placeholder="请输入加入黑名单的原因"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitCreate">确认添加</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  createDeviceBlacklist,
  listDeviceBlacklist,
  updateDeviceBlacklist,
  updateDeviceBlacklistStatus,
} from '@/api/mall';

export default {
  name: 'MallDeviceBlacklist',
  data() {
    return {
      loading: false,
      submitting: false,
      records: [],
      total: 0,
      query: {
        deviceId: '',
        enabled: null,
        current: 1,
        pageSize: 20,
      },
      createVisible: false,
      createForm: {
        deviceText: '',
        remark: '',
      },
      createRules: {
        deviceText: [
          { required: true, message: '请输入设备号', trigger: 'blur' },
          { validator: this.validateDeviceText, trigger: 'blur' },
        ],
      },
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    parseDeviceIds(text) {
      return [...new Set(
        String(text || '')
          .split(/[,\n，]/)
          .map(item => item.trim())
          .filter(Boolean),
      )];
    },
    validateDeviceText(rule, value, callback) {
      const deviceIds = this.parseDeviceIds(value);
      if (deviceIds.length === 0) {
        callback(new Error('请输入设备号'));
      } else if (deviceIds.length > 100) {
        callback(new Error('单次最多添加100个设备号'));
      } else {
        callback();
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await listDeviceBlacklist({
          deviceId: this.query.deviceId || undefined,
          enabled: this.query.enabled,
          current: this.query.current,
          pageSize: this.query.pageSize,
        });
        const page = res.data || {};
        this.records = page.records || [];
        this.total = page.total || 0;
      } catch (error) {
        // 业务错误已由全局 Axios 拦截器提示，此处消费异常，避免开发环境错误遮罩。
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.query.current = 1;
      this.loadData();
    },
    resetSearch() {
      this.query.deviceId = '';
      this.query.enabled = null;
      this.query.current = 1;
      this.loadData();
    },
    handleCurrentChange(current) {
      this.query.current = current;
      this.loadData();
    },
    handleSizeChange(pageSize) {
      this.query.pageSize = pageSize;
      this.query.current = 1;
      this.loadData();
    },
    openCreateDialog() {
      this.createForm = { deviceText: '', remark: '' };
      this.createVisible = true;
      this.$nextTick(() => this.$refs.createFormRef?.clearValidate());
    },
    async submitCreate() {
      const valid = await this.$refs.createFormRef.validate().catch(() => false);
      if (!valid) return;
      this.submitting = true;
      try {
        await createDeviceBlacklist({
          deviceIds: this.parseDeviceIds(this.createForm.deviceText),
          remark: this.createForm.remark.trim() || null,
        });
        ElMessage.success('设备黑名单添加成功');
        this.createVisible = false;
        this.query.current = 1;
        await this.loadData();
      } catch (error) {
        // 保留弹窗和输入内容，便于用户根据全局错误提示修正设备号后重试。
      } finally {
        this.submitting = false;
      }
    },
    async editRemark(row) {
      try {
        const { value } = await ElMessageBox.prompt(
          `修改设备 ${row.deviceId} 的备注`,
          '编辑备注',
          {
            inputValue: row.remark || '',
            inputPlaceholder: '请输入备注',
            inputValidator: value => !value || value.length <= 255 || '备注不能超过255个字符',
            confirmButtonText: '保存',
            cancelButtonText: '取消',
          },
        );
        await updateDeviceBlacklist(row.id, { remark: value.trim() || null });
        ElMessage.success('备注已更新');
        await this.loadData();
      } catch (error) {
        // 取消操作或接口业务错误均无需继续向上抛出。
      }
    },
    async toggleStatus(row) {
      const nextEnabled = row.enabled === 1 ? 0 : 1;
      const action = nextEnabled === 1 ? '拉黑设备' : '取消拉黑';
      try {
        await ElMessageBox.confirm(
          `确认对设备 ${row.deviceId} 执行“${action}”吗？`,
          `${action}确认`,
          { type: nextEnabled === 1 ? 'warning' : 'info' },
        );
        await updateDeviceBlacklistStatus(row.id, nextEnabled);
        ElMessage.success(action === '拉黑设备' ? '设备已拉黑' : '已取消拉黑');
        await this.loadData();
      } catch (error) {
        // 取消操作或接口业务错误均无需继续向上抛出。
      }
    },
    maskPhone(phone) {
      if (!phone) return '-';
      const value = String(phone);
      return value.length >= 7
        ? `${value.slice(0, 3)}****${value.slice(-4)}`
        : value;
    },
  },
};
</script>

<style scoped>
.device-blacklist {
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;
}

.page-header h2 {
  margin: 0 0 8px;
}

.page-header p {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.search-form {
  margin-bottom: 4px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

small {
  color: #909399;
}

.inspector-item {
  display: flex;
  gap: 8px;
  align-items: baseline;
}

.inspector-item + .inspector-item {
  margin-top: 4px;
}
</style>
